import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import { promoBenefits, promoEndsLabel } from "@/lib/promo";
import { usePromo } from "@/lib/use-promo";

const dismissKey = (id: string) => `kanata:promo-bar-closed:${id}`;

function readDismissed(id: string): boolean {
  try {
    return sessionStorage.getItem(dismissKey(id)) === "1";
  } catch {
    return false;
  }
}

/**
 * The live automatic promotion, in a thin bar above the header on every page,
 * linking to the desks it applies to. Divine: the paket links to "Meja" so the
 * customer picks a variant and a size.
 *
 * Kept off checkout and the payment page, where the only thing left to do is
 * pay and the promotion is already in the total. Closing it hides it for the
 * rest of the visit; a new promotion shows again.
 */
export function AnnouncementBar() {
  const promo = usePromo();
  const { pathname } = useLocation();
  const [closed, setClosed] = useState(true);

  useEffect(() => {
    if (promo) setClosed(readDismissed(promo.id));
  }, [promo]);

  if (!promo || closed || pathname.startsWith("/checkout") || pathname.startsWith("/bayar")) return null;

  const ends = promoEndsLabel(promo.endsAt);
  function close() {
    setClosed(true);
    try {
      if (promo) sessionStorage.setItem(dismissKey(promo.id), "1");
    } catch {
      // A private window without storage just sees it again next page.
    }
  }

  return (
    <div className="bg-brand-dark text-white">
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-5 py-2 text-xs sm:px-8 sm:text-sm">
        <Link to="/katalog?jenis=meja" className="group flex min-w-0 flex-1 items-center gap-2">
          <span className="truncate">
            <span className="font-medium">{promo.name}</span>
            <span className="text-white/80">
              {" "}
              · {promoBenefits(promo)} untuk setiap meja
              {ends ? `, sampai ${ends}` : ""}
            </span>
          </span>
          <span className="hidden shrink-0 items-center gap-1 font-medium underline-offset-2 group-hover:underline sm:inline-flex">
            Pilih meja
            <ArrowRight className="size-3.5" />
          </span>
        </Link>
        <button
          type="button"
          onClick={close}
          aria-label="Tutup pengumuman"
          className="shrink-0 rounded-full p-1 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
        >
          <X className="size-3.5" />
        </button>
      </div>
    </div>
  );
}
